import {
  AmendmentVisibility as AmendmentVisibilityProto,
  type BulkCopyJobResponse,
  type CopyJobResponse as CopyJobResponseProto,
  Job as JobProto,
  Resolution as ResolutionProto,
  Status as StatusProto,
  WaitingOn as WaitingOnProto,
} from '@taxfyle/api-internal/internal/job_pb'
import { JobEvent as JobEventProto } from '@taxfyle/api-internal/internal/job_event_pb'
import type {
  BulkAdvanceJobsResponse,
  BulkArchiveJobsResponse,
  BulkRestoreJobsResponse,
  BulkTransferJobsResponse,
} from '@taxfyle/api-internal/internal/job_admin_pb'
import { difference } from 'lodash-es'
import { BadRequest } from 'fejl'
import {
  type Allocation,
  type Amendment,
  type AmendmentVisibility,
  type CollaboratorRole,
  type CollaboratorType,
  type JobBillingType,
  type JobLegendServiceType,
  type JobReturnAmountsSource,
  type JobReturnAmountsYear,
  type JobScopeItem,
  type JobUser,
  type PayoutSplit,
  type PaymentInfoStatus,
  type RatingConfig,
  type RatingMethod,
  type Resolution,
  type WaitingOn,
} from '@/features/jobs/Job'
import { type JobType } from '@/features/jobs/job-type'
import { type JobStatus } from '@/features/jobs/job-status'
import type {
  JobEventProps,
  JobEventType,
  JobEventVisibility,
} from '@/features/jobs/JobEvent'
import type { PrimeUserFn } from '@/features/iam'
import type {
  BulkAdvanceJobsResult,
  BulkCopyJobsResult,
  CopyJobResponse,
} from '@/features/jobs/JobStore'
import type { Id } from '@/infra/id'
import {
  maybeDecimalToNumber,
  safeDecimalToNumber,
  safeTimestampToDate,
  timestampToDate,
} from '@/utils/grpc-util'
import { unixEpoch } from '@/utils/date-util'
import { match } from '@/utils/pattern-match-util'

export function mapJobStatusProtoToJobStatus(status: StatusProto): JobStatus {
  return match(status, {
    [StatusProto.UNDER_CONSTRUCTION]: () => 'UnderConstruction',
    [StatusProto.UNCLAIMED]: () => 'Unclaimed',
    [StatusProto.CLAIMED]: () => 'Claimed',
    [StatusProto.CLOSED]: () => 'Closed',
    [StatusProto.IDLE]: () => 'Idle',
    [StatusProto.ON_HOLD]: () => 'OnHold',
    [StatusProto.INFO_GATHERING]: () => 'InfoGathering',
    [StatusProto.PENDING]: () => 'Pending',
    [StatusProto.STATUS_UNSPECIFIED]: () => {
      throw new BadRequest('Job is of an undefined status')
    },
  })
}

export function mapResolutionProtoToResolution(
  resolution: ResolutionProto,
): Resolution | null {
  return match(resolution, {
    [ResolutionProto.RESOLUTION_UNSPECIFIED]: () => null,
    [ResolutionProto.DONE]: () => 'Done',
    [ResolutionProto.CANCELLED]: () => 'Cancelled',
    [ResolutionProto.TEST]: () => 'Test',
  })
}

export function mapPaymentInfoStatusProtoToPaymentInfoStatus(
  status: JobProto.PaymentInfoStatus,
): PaymentInfoStatus {
  return match(status, {
    [JobProto.PaymentInfoStatus.NOT_PROVIDED]: () => 'NotProvided',
    [JobProto.PaymentInfoStatus.PROVIDED]: () => 'Provided',
    [JobProto.PaymentInfoStatus.REQUESTED]: () => 'Requested',
  })
}

export function mapWaitingOnProtoToWaitingOn(
  waitingOn: WaitingOnProto,
): WaitingOn | null {
  return match(waitingOn, {
    [WaitingOnProto.WAITING_ON_CLIENT]: () => 'Client',
    [WaitingOnProto.WAITING_ON_PRO]: () => 'Provider',
    [WaitingOnProto.WAITING_ON_UNSPECIFIED]: () => null,
  })
}

function mapAmendmentVisibilityProtoToAmendmentVisibility(
  visibility: AmendmentVisibilityProto,
): AmendmentVisibility {
  return match(visibility, {
    [AmendmentVisibilityProto.INTERNAL]: () => 'Internal',
    [AmendmentVisibilityProto.PUBLIC]: () => 'Public',
  })
}

export function mapMemberTypeProtoToCollaboratorType(
  type: JobProto.MemberType,
): CollaboratorType {
  return match(type, {
    [JobProto.MemberType.CLIENT]: () => 'Client',
    [JobProto.MemberType.PROVIDER]: () => 'Pro',
  })
}

export function mapMemberRoleProtoToCollaboratorRole(
  role: JobProto.MemberRole,
): CollaboratorRole {
  return match(role, {
    [JobProto.MemberRole.CHAMPION]: () => 'Champion',
    [JobProto.MemberRole.SUPPORTING]: () => 'Supporting',
  })
}

export function mapJobTypeProtoToJobType(proto: JobProto.JobType): JobType {
  if (proto.getConsultation()) {
    return { type: 'Consultation' }
  }

  if (proto.getBookkeeping()) {
    return { type: 'Bookkeeping' }
  }

  if (proto.getMisc()) {
    return { type: 'Misc' }
  }

  const tax = proto.getTax()
  if (tax) {
    const jobLinks = tax.getJobLinks()
    const prepJobLink = jobLinks?.getPrepJobLink()
    if (prepJobLink) {
      return {
        type: 'Tax',
        jobLinks: {
          prepAndReviewLink: { type: 'Prep', id: prepJobLink.getId() },
        },
      }
    }

    const reviewJobLink = jobLinks?.getReviewJobLink()
    if (reviewJobLink) {
      return {
        type: 'Tax',
        jobLinks: {
          prepAndReviewLink: { type: 'Review', id: reviewJobLink.getId() },
        },
      }
    }

    return { type: 'Tax', jobLinks: null }
  }

  return { type: 'Unspecified' }
}

export function mapRatingConfigProtoToProps(
  proto: JobProto.Legend.RatingConfig,
): RatingConfig {
  return {
    platformRatingEnabled: proto.getPlatformRatingEnabled(),
    proRatingEnabled: proto.getProRatingEnabled(),
    customerRatingMethod: mapCustomerRatingMethodProtoToRatingMethod(
      proto.getCustomerRatingMethod(),
    ),
  }
}

function mapCustomerRatingMethodProtoToRatingMethod(
  method: JobProto.Legend.RatingConfig.CustomerRatingMethod,
): RatingMethod {
  return match(method, {
    [JobProto.Legend.RatingConfig.CustomerRatingMethod.STARS]: () => 'Stars',
    [JobProto.Legend.RatingConfig.CustomerRatingMethod.THUMBS]: () => 'Thumbs',
    [JobProto.Legend.RatingConfig.CustomerRatingMethod.UNSPECIFIED]: () =>
      'Thumbs',
  })
}

export function mapLegendServiceTypeProtoToJobLegendServiceType(
  type: JobProto.Legend.LegendServiceType,
): JobLegendServiceType {
  return match(type, {
    [JobProto.Legend.LegendServiceType.TAX]: () => 'Tax',
    [JobProto.Legend.LegendServiceType.BOOKKEEPING]: () => 'Bookkeeping',
    [JobProto.Legend.LegendServiceType.CONSULTATION]: () => 'Consultation',
    [JobProto.Legend.LegendServiceType.MISC]: () => 'Misc',
    [JobProto.Legend.LegendServiceType.LEGEND_SERVICE_TYPE_UNSPECIFIED]: () =>
      'Unspecified',
  })
}

export function mapBillingTypeProtoToJobBillingType(
  billingType: JobProto.BillingType,
): JobBillingType {
  return match(billingType, {
    [JobProto.BillingType.EXTERNAL]: () => 'External',
    [JobProto.BillingType.STRIPE]: () => 'Stripe',
  })
}

function mapReturnAmountsSourceProtoToJobReturnAmountsSource(
  source: JobProto.ReturnAmountsSource,
): JobReturnAmountsSource {
  return match(source, {
    [JobProto.ReturnAmountsSource.RETURN_AMOUNTS_SOURCE_EXTRACTED]: () =>
      'Extracted',
    [JobProto.ReturnAmountsSource.RETURN_AMOUNTS_SOURCE_MANUAL]: () => 'Manual',
    [JobProto.ReturnAmountsSource.RETURN_AMOUNTS_SOURCE_UNSPECIFIED]: () =>
      'Unspecified',
  })
}

export function mapReturnAmountsYearProtoToProps(
  proto: JobProto.ReturnAmountsYear,
): JobReturnAmountsYear {
  return {
    taxYear: proto.getTaxYear(),
    federalAmount: maybeDecimalToNumber(proto.getFederalAmount()),
    stateAmounts: proto.getAmountByStateList().map((s) => ({
      state: s.getState(),
      amount: safeDecimalToNumber(s.getAmount()),
      source: mapReturnAmountsSourceProtoToJobReturnAmountsSource(
        s.getSource(),
      ),
    })),
    federalSource: mapReturnAmountsSourceProtoToJobReturnAmountsSource(
      proto.getSource(),
    ),
  }
}

export function mapAmendmentProtoToProps(proto: JobProto.Amendment): Amendment {
  const creator = proto.getCreator()
  return {
    creator: creator
      ? mapJobUserProtoToProps(creator)
      : {
          email: '',
          familyName: '',
          displayName: '',
          givenName: '',
          picture: '',
          userId: '',
          userPublicId: '',
        },
    customerAmount: safeDecimalToNumber(proto.getCustomerAmount()),
    dateCreated: timestampToDate(proto.getDateCreated()) ?? unixEpoch(),
    description: proto.getDescription(),
    internalNote: proto.getInternalNote(),
    providerAmount: safeDecimalToNumber(proto.getProviderAmount()),
    visibility: mapAmendmentVisibilityProtoToAmendmentVisibility(
      proto.getVisibility(),
    ),
  }
}

function mapJobUserProtoToProps(proto: JobProto.Member): JobUser {
  return {
    email: proto.getEmail(),
    familyName: proto.getFamilyName(),
    displayName: proto.getDisplayName(),
    givenName: proto.getGivenName(),
    picture: proto.getPicture(),
    userId: proto.getUserId(),
    userPublicId: proto.getId(),
  }
}

export function mapJobScopeItemProtoToProps(
  proto: JobProto.JobScopeItem,
): JobScopeItem {
  return {
    id: proto.getId(),
    name: proto.getName(),
    description: proto.getDescription(),
    quantity: proto.getQuantity(),
    courtesyQuantity: proto.getCourtesyQuantity()?.getValue(),
    price: maybeDecimalToNumber(proto.getPrice()),
    proPrice: maybeDecimalToNumber(proto.getProPrice()),
    inventoryItemId: proto.getInventoryItemId(),
    inventoryItemVersionId: proto.getInventoryItemVersionId(),
    bundleId: proto.getBundleId()?.getValue() ?? null,
    customItemId: proto.getCustomItemId()?.getValue() ?? null,
  }
}

export function mapPayoutSplitProtoToProps(
  proto: JobProto.PayoutSplit,
  primeUser: PrimeUserFn,
): PayoutSplit {
  const allocations: Allocation[] = proto
    .getAllocationsList()
    .map((allocation) => ({
      amount: safeDecimalToNumber(allocation.getAmount()),
      authorizedPartialPayoutAmount: safeDecimalToNumber(
        allocation.getAuthorizedPartialPayoutAmount(),
      ),
      user: primeUser({ publicId: allocation.getUserPublicId() }),
    }))

  if (proto.getAutoAdjusted()) {
    return {
      allocations,
      situation: {
        type: 'Auto',
        adjustedAt: timestampToDate(proto.getDateAutoAdjusted()) ?? unixEpoch(),
      },
    }
  }

  const allocatedManuallyByPublicId = proto
    .getAllocatedManuallyByPublicId()
    ?.getValue()
  if (allocatedManuallyByPublicId) {
    return {
      allocations,
      situation: {
        type: 'Manual',
        allocatedBy: primeUser({ publicId: allocatedManuallyByPublicId }),
        allocatedAt:
          timestampToDate(proto.getDateAllocatedManually()) ?? unixEpoch(),
      },
    }
  }

  return {
    allocations,
    situation: { type: 'Default' },
  }
}

/**
 * Maps the per-job failures of a bulk transfer.
 */
export function mapBulkTransferJobsResponseProtoToErrors(
  proto: BulkTransferJobsResponse,
) {
  return proto.getErrorsList().map((e) => ({
    jobId: e.getJobid(),
    error: e.getError(),
  }))
}

/**
 * Maps a job event proto to the props of a job event, apart from the job and
 * the member that triggered it.
 */
export function mapJobEventProtoToProps(
  proto: JobEventProto,
): Omit<JobEventProps, 'job' | 'triggeredBy'> {
  return {
    id: proto.getId(),
    dateCreated: safeTimestampToDate(proto.getDateCreated()),
    eventType: mapJobEventTypeProtoToJobEventType(proto.getEventType()),
    eventVisibility: mapJobEventVisibilityProtoToJobEventVisibility(
      proto.getEventVisibility(),
    ),
    description: proto.getDescription(),
    reason: proto.getReason()?.getValue() || '',
    taskTitle: proto.getTaskTitle()?.getValue(),
    previousProvider: proto.getPreviousProvider()?.getDisplayName(),
    newTeam: proto.getNewTeam()?.getName(),
    jobResolution: mapJobEventResolutionProtoToResolution(
      proto.getJobResolution(),
    ),
    newProvider: proto.getNewProvider()?.getDisplayName(),
    newClient: proto.getNewClient()?.getDisplayName(),
    provider: proto.getProvider()?.getDisplayName(),
  }
}

function mapJobEventTypeProtoToJobEventType(
  eventType: JobEventProto.EventType,
): JobEventType {
  // prettier-ignore
  return match(eventType, {
    [JobEventProto.EventType.EVENT_TYPE_UNSPECIFIED]: () => 'Unspecified',
    [JobEventProto.EventType.UNCLAIMED]: () => 'Unclaimed',
    [JobEventProto.EventType.CLAIMED]: () => 'Claimed',
    [JobEventProto.EventType.UPDATED]: () => 'Updated',
    [JobEventProto.EventType.AMENDED]: () => 'Amended',
    [JobEventProto.EventType.REOPENED]: () => 'Reopened',
    [JobEventProto.EventType.TRANSFERRED]: () => 'Transferred',
    [JobEventProto.EventType.FORFEITED]: () => 'Forfeited',
    [JobEventProto.EventType.PROVIDER_ADDED]: () => 'ProviderAdded',
    [JobEventProto.EventType.PROVIDER_REMOVED]: () => 'ProviderRemoved',
    [JobEventProto.EventType.JOB_MEMBER_ADDED]: () => 'JobMemberAdded',
    [JobEventProto.EventType.JOB_MEMBER_REMOVED]: () => 'JobMemberRemoved',
    [JobEventProto.EventType.TASK_MARKED_COMPLETE]: () => 'TaskMarkedComplete',
    [JobEventProto.EventType.TASK_MARKED_INCOMPLETE]: () => 'TaskMarkedIncomplete',
    [JobEventProto.EventType.CLOSED]: () => 'Closed',
    [JobEventProto.EventType.RESUMED_JOB]: () => 'ResumedJob',
    [JobEventProto.EventType.PUT_ON_HOLD]: () => 'PutOnHold',
    [JobEventProto.EventType.BECAME_INACTIVE]: () => 'BecameInactive',
    [JobEventProto.EventType.PAYMENT_INFO_REQUESTED]: () => 'PaymentInfoRequested',
    [JobEventProto.EventType.PAYMENT_INFO_PROVIDED]: () => 'PaymentInfoProvided',
    [JobEventProto.EventType.PAYMENT_SUCCESSFUL]: () => 'PaymentSuccessful',
    [JobEventProto.EventType.PAYMENT_FAILED]: () => 'PaymentFailed',
    [JobEventProto.EventType.COUPON_ADDED]: () => 'CouponAdded',
    [JobEventProto.EventType.COUPON_REMOVED]: () => 'CouponRemoved',
    [JobEventProto.EventType.REFUND_SUCCESSFUL]: () => 'RefundSuccessful',
    [JobEventProto.EventType.REFUND_FAILED]: () => 'RefundFailed',
    [JobEventProto.EventType.MANUAL_INVOICE_ADDED]: () => 'ManualInvoiceAdded',
    [JobEventProto.EventType.ANSWERS_CORRECTED]: () => 'AnswersCorrected',
    [JobEventProto.EventType.INFO_GATHERING_COMPLETED]: () => 'InfoGatheringCompleted',
    [JobEventProto.EventType.CLIENT_ADDED]: () => 'ClientAdded',
    [JobEventProto.EventType.BECAME_ACTIVE]: () => 'Unspecified' as JobEventType,
    [match.DEFAULT]: () => 'Unspecified' as JobEventType
  })
}

function mapJobEventVisibilityProtoToJobEventVisibility(
  eventVisibility: JobEventProto.EventVisibility,
): JobEventVisibility {
  return match(eventVisibility, {
    [JobEventProto.EventVisibility.PUBLIC]: () => 'Public',
    [JobEventProto.EventVisibility.INTERNAL]: () => 'Internal',
  })
}

function mapJobEventResolutionProtoToResolution(
  resolution: JobEventProto.JobResolution,
) {
  const mappedResolution: Resolution | null = match(resolution, {
    [JobEventProto.JobResolution.DONE]: () => 'Done',
    [JobEventProto.JobResolution.CANCELLED]: () => 'Cancelled',
    [JobEventProto.JobResolution.TEST]: () => 'Test',
    [JobEventProto.JobResolution.JOB_RESOLUTION_UNSPECIFIED]: () => null,
  })

  return mappedResolution ?? undefined
}

/**
 * Maps the draft that copying a job produced.
 */
export function mapCopyJobResponseProtoToResponse(
  proto: CopyJobResponseProto,
): CopyJobResponse {
  return {
    jobId: proto.getId(),
    readyForSubmission: proto.getReadyForSubmission(),
  }
}

/**
 * Maps the copies and per-job failures of a bulk copy.
 */
export function mapBulkCopyJobResponseProtoToResult(
  proto: BulkCopyJobResponse,
): BulkCopyJobsResult {
  return {
    copiedJobIds: proto.getIdsList(),
    errors: proto.getErrorsList().map((error) => ({
      jobId: error.getId(),
      error: error.getError(),
    })),
  }
}

/**
 * Maps the outcome of a bulk archive. Requested jobs the server did not
 * reject were archived.
 *
 * @param ids The requested job IDs.
 * @param proto
 */
export function mapBulkAdvanceJobsResponseProtoToResult(
  ids: Id[],
  proto: BulkAdvanceJobsResponse,
): BulkAdvanceJobsResult {
  const skippedIds = proto.getSkippedIdsList()
  const errors = proto.getErrorsList().map((e) => ({
    jobId: e.getJobId(),
    error: e.getError(),
  }))
  return {
    skippedIds,
    errors,
    advancedIds: difference(ids, [
      ...skippedIds,
      ...errors.map((e) => e.jobId),
    ]),
  }
}

export function mapBulkArchiveJobsResponseProtoToResult(
  ids: Id[],
  proto: BulkArchiveJobsResponse,
) {
  const denied = proto.getDeniedIdsList()
  const alreadyArchived = proto.getAlreadyArchivedIdsList()
  const submissionInProgress = proto.getSubmissionInProgressIdsList()
  const notFound = proto.getNotFoundIdsList()
  const transactionsFound = proto.getTransactionsFoundIdsList()
  return {
    denied,
    alreadyArchived,
    submissionInProgress,
    notFound,
    transactionsFound,
    archived: difference(ids, [
      ...denied,
      ...alreadyArchived,
      ...submissionInProgress,
      ...transactionsFound,
      ...notFound,
    ]),
  }
}

/**
 * Maps the outcome of a bulk restore. Requested jobs the server did not
 * reject were restored.
 *
 * @param ids The requested job IDs.
 * @param proto
 */
export function mapBulkRestoreJobsResponseProtoToResult(
  ids: Id[],
  proto: BulkRestoreJobsResponse,
) {
  const denied = proto.getDeniedIdsList()
  const submissionInProgress = proto.getSubmissionInProgressIdsList()
  const notFound = proto.getNotFoundIdsList()
  return {
    denied,
    submissionInProgress,
    notFound,
    restored: difference(ids, [
      ...denied,
      ...submissionInProgress,
      ...notFound,
    ]),
  }
}
